import { useState } from 'react'
import { Box, Button, Collapsible, HStack, Icon, Progress, Text, VStack } from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiPlus } from 'react-icons/fi'
import { useLobbyDayEvent } from '../../../hooks/useLobbyDay'
import { useTargetCoverage } from '../../../hooks/useLobbyRecruit'
import { partitionCoverage } from './coverage'
import type { ScheduleMeetingDto } from '../../../types/lobbyDay'

interface LegislatorCoveragePanelProps {
  eventId: string
  meetings: ScheduleMeetingDto[]
  onAddMeeting: (legislatorId: string) => void
  defaultOpen?: boolean
}

/** Which target legislators still have no meeting. Self-contained on the same data source
 *  Auto-build uses (target coverage + event.targetAllLegislators). Shared desktop + mobile. */
export function LegislatorCoveragePanel({
  eventId, meetings, onAddMeeting, defaultOpen = true,
}: LegislatorCoveragePanelProps) {
  const { data: coverage } = useTargetCoverage(eventId)
  const { data: event } = useLobbyDayEvent(eventId)
  const [open, setOpen] = useState(defaultOpen)

  if (!coverage || !event) return null
  const { total, scheduled, unscheduled } = partitionCoverage(coverage, event.targetAllLegislators, meetings)
  if (total === 0) return null

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="surface" p={3} w="full">
      <HStack as="button" w="full" justify="space-between" onClick={() => setOpen((o) => !o)} {...({ type: 'button' } as Record<string, unknown>)}>
        <Text fontWeight="semibold" fontSize="sm">
          <Text as="span" color="accent">{scheduled} / {total}</Text> targets scheduled
        </Text>
        <HStack gap={1} color="gray.500" fontSize="xs">
          {unscheduled.length > 0 && <Text>{unscheduled.length} left</Text>}
          <Icon as={open ? FiChevronDown : FiChevronRight} />
        </HStack>
      </HStack>
      <Progress.Root
        value={total ? (scheduled / total) * 100 : 0}
        size="xs"
        colorPalette="green"
        mt={2}
        borderRadius="full">
        <Progress.Track>
          <Progress.Range />
        </Progress.Track>
      </Progress.Root>
      <Collapsible.Root open={open}>
        <Collapsible.Content>
          {unscheduled.length === 0 ? (
            <Text fontSize="sm" color="green.600" mt={3}>Every target has a meeting.</Text>
          ) : (
            <VStack align="stretch" gap={1} mt={3}>
              {unscheduled.map((r) => (
                <HStack key={r.legislatorId} justify="space-between" gap={2}>
                  <Box minW={0}>
                    <Text fontSize="sm" lineClamp={1}>
                      {r.name}
                      {r.isPriority && (
                        <Text as="span" color="orange.500" fontSize="2xs" fontWeight="bold" ml={1}>PRIORITY</Text>
                      )}
                    </Text>
                    <Text fontSize="xs" color="gray.500" lineClamp={1}>{r.districtLabel}</Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="outline"
                    colorPalette="brand"
                    flexShrink={0}
                    minH={{ base: '44px', md: 'auto' }}
                    onClick={() => onAddMeeting(r.legislatorId)}><FiPlus />meeting
                                    </Button>
                </HStack>
              ))}
            </VStack>
          )}
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
